import { attr, belongsTo, hasMany } from "@ember-data/model";
import dayjs from "dayjs";
import {
  validateDate,
  validateInclusion,
  validatePresence
} from "ember-changeset-validations/validators";
import type IntlService from "ember-intl/services/intl";
import JournalEvent from "parog-web/models/journal-event";
import Patient from "parog-web/models/patient";
import { sortBy } from "parog-web/utils/sort";
import tWithDefault from "parog-web/utils/t-with-default";
import type ApicryptMessageReceivedJournalEvent from "./apicrypt-message-received-journal-event";
import type BioExamResult from "./bio-exam-result";
import type BioExamResultsApprovedJournalEvent from "./bio-exam-results-approved-journal-event";
import type FileAttachment from "./file-attachment";
import type { Summary } from "./journal-event";

//---- Automatically generated, don't update this part ----

type OruStatusStringEnum = "F" | "P" | "M" | "I" | "R" | "C" | "O" | "D" | "X";

type MatchingPageTypeStringEnum = "medics_web" | "matchlab";

export default class BioExamResultsReceivedJournalEvent extends JournalEvent {
  static override type = "bio-exam-results-received-journal-event";

  @attr("string") declare rawOru: string;

  @attr("string") declare oruId: string;

  @attr("string") declare oruStatus: OruStatusStringEnum;

  @attr("boolean", {
    allowNull: true
  })
  declare isFromHprim: boolean;

  @attr("boolean", {
    allowNull: true
  })
  declare isFromOru: boolean;

  /**
   * Whether the JE has been validated by an IDE or not.
   */
  @attr("boolean", {
    allowNull: true
  })
  declare manualValidationDone: boolean;

  /**
   * HLA result ID
   */
  @attr("string") declare hlaId: string;

  /**
   * When the HLA consultation report has been edited
   */
  @attr("date") declare hlaCrDate: Date;

  @attr("string") declare matchingPageType: MatchingPageTypeStringEnum;

  @attr("string") declare lab: string;

  @attr("boolean", {
    allowNull: true
  })
  declare normalized: boolean;

  /**
   * Wether or not the JE must be validated manually by an IDE
   */
  @attr("boolean", {
    allowNull: true
  })
  declare requiresManualValidation: boolean;

  /**
   * Biology prescriptor extracted from the LLM call on the associated PDF
   */
  @attr("string") declare prescriptor: string;

  @hasMany("bio-exam-results-approved-journal-event", {
    async: false,
    inverse: "bioExamResultsReceivedJournalEvent"
  })
  declare bioExamResultsApprovedJournalEvents: BioExamResultsApprovedJournalEvent[];

  @hasMany("bio-exam-result", {
    async: false,
    inverse: "bioExamResultsReceivedJournalEvent"
  })
  declare bioExamResults: BioExamResult[];

  static override validations(intl: IntlService) {
    return {
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.bio-exam-results-received-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date",
          onOrBefore: dayjs().endOf("day").toDate()
        })
      ],
      oruStatus: validateInclusion({
        list: ["F", "P", "M", "I", "R", "C", "O", "D", "X"],
        allowBlank: true,
        description: intl.t(
          "validations.bio-exam-results-received-journal-event.oru-status"
        )
      }),
      matchingPageType: validateInclusion({
        list: ["medics_web", "matchlab"],
        allowBlank: true,
        description: intl.t(
          "validations.bio-exam-results-received-journal-event.matching-page-type"
        )
      })
    };
  }

  //---- End of generated code ----
  @attr("bio-exam-results") declare bioExamResultsAttributes: BioExamResult[];

  @belongsTo("patient", {
    async: false,
    inverse: "bioExamResultsReceivedJournalEvents"
  })
  declare patient: Patient;

  @hasMany("apicrypt-message-received-journal-event", {
    async: false,
    inverse: "bioExamResultsReceivedJournalEvent"
  })
  declare apicryptMessageReceivedJournalEvents: ApicryptMessageReceivedJournalEvent[];

  get lastApicryptMessage(): ApicryptMessageReceivedJournalEvent {
    return this.apicryptMessageReceivedJournalEvents
      .toSorted(sortBy("createdAt"))
      .at(-1) as ApicryptMessageReceivedJournalEvent;
  }

  @hasMany("file-attachment", {
    async: false,
    inverse: null
  })
  declare fileAttachmentsWithoutZip: FileAttachment[];

  override get orderedFileAttachments(): FileAttachment[] {
    return this.fileAttachmentsWithoutZip.toSorted(sortBy("index"));
  }

  /**
   * Find the BioExamResult with the given code
   */
  result(code: string): BioExamResult | undefined {
    return this.bioExamResults.find(r => r.code === code);
  }

  hasPersistedResult(code: string): boolean {
    return !this.isSaving && Boolean(this.result(code)?.id);
  }

  hasAlertForCode(code: string): boolean {
    if (!code || !this.isAlertingJournalEvent) return false;

    const normalizedCode = code.replace("LOINC-", "");
    return Boolean(
      this.alertTriggeredJournalEvents.find(alert => {
        return alert.code == normalizedCode;
      })
    );
  }

  override get summary(): Summary {
    return Object.assign({}, super.summary, {
      forcIdentifier: {
        feature: "bio-results",
        component: "edition"
      }
    });
  }

  get showCorrection(): boolean {
    return (
      this.oruStatus === "C" && this.validationStatus == "isNotYetValidated"
    );
  }

  get isFromExternalSource(): boolean {
    return this.isFromHprim || this.isFromOru || Boolean(this.hlaId);
  }

  get sourceFileType(): string | undefined {
    if (this.isFromHprim) return "hprim";
    if (this.isFromOru) return "oru";

    return;
  }

  get validationStatus():
    | "isNotYetValidated"
    | "isValidated"
    | "isPassivelyValidated"
    | "patientDeclaration"
    | "medicDeclaration" {
    // when requiresManualValidation is undefined
    // there are still some cases (for old JEs) where the manualValidationDone is true
    // that's why we kept the condition & the early return line 226,
    // but then if it's not true we go check the creator as requested by the product
    if (this.requiresManualValidation == undefined) {
      if (this.manualValidationDone) return "isValidated";
      if (this.creator)
        return this.creator instanceof Patient
          ? "patientDeclaration"
          : "medicDeclaration";
      return "isPassivelyValidated";
    }

    // If the JE has `requiresManualValidation` to false
    // then the validation status is always `isPassivelyValidated`
    if (!this.requiresManualValidation) {
      return "isPassivelyValidated";
    }

    // If the JE has `requiresManualValidation` to true
    // the validation status depends on the `manualValidationDone` attribute
    return this.manualValidationDone ? "isValidated" : "isNotYetValidated";
  }

  get displayableBioExamResults() {
    // filter the results to display only those matched by experience, as other match type might be irrelevant while draft
    return this.bioExamResults.filter(
      bioExamResult =>
        !bioExamResult.isDraft || bioExamResult.matchedByExperience
    );
  }

  async approve(): Promise<void> {
    this.manualValidationDone = true;
    const bioExamResults = this.bioExamResults.map(res => {
      res.isDraft = false;
      return res;
    });
    this.bioExamResults = bioExamResults;
    this.save();

    await this.store
      .createRecord("bio-exam-results-approved-journal-event", {
        bioExamResultsReceivedJournalEvent: this,
        patient: this.patient,
        occurredAt: new Date()
      })
      .save();
  }

  get canEdit(): boolean {
    return !this.hlaId;
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "bio-exam-results-received-journal-event": BioExamResultsReceivedJournalEvent;
  }
}
